import { mapGeoJobRowToFloodJob } from '@/features/analytics/utils/mapGeoJobRowToFloodJob';
import type { FloodJob } from '@/mocks/floodJobs';
import { geoProcessingApi } from '@/stores/geoProcessingApi';

export const geoProcessingAnalysisApi = geoProcessingApi.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /api/flood-detection/jobs — danh sách job flood từ geo-processing. */
    getGeoFloodJobs: builder.query<FloodJob[], { page?: number; page_size?: number } | void>({
      query: (params) => ({
        url: '/api/flood-detection/jobs',
        params: {
          page: params?.page ?? 1,
          page_size: params?.page_size ?? 1000,
        },
      }),
      transformResponse: (raw: any) => {
        const items = raw?.data?.items || raw?.items || [];
        return items.map(mapGeoJobRowToFloodJob);
      },
      providesTags: [{ type: 'FloodJob', id: 'LIST' }],
    }),

    /** GET /api/flood-detection/jobs/{id} — Trạng thái chi tiết job. */
    getGeoFloodJobById: builder.query<FloodJob, string>({
      query: (id) => ({ url: `/api/flood-detection/jobs/${encodeURIComponent(id)}` }),
      transformResponse: (raw: any) => {
        const data = raw?.data || raw;
        return mapGeoJobRowToFloodJob(data);
      },
      providesTags: (_r, _e, id) => [{ type: 'FloodJob', id }],
    }),

    /** POST /api/flood-detection/analyze/sar — Chạy phân tích SAR flood mapping. */
    postGeoSarAnalyze: builder.mutation<Record<string, any>, Record<string, any>>({
      query: (body) => ({
        url: '/api/flood-detection/analyze/sar',
        method: 'POST',
        data: body,
      }),
      transformResponse: (raw: any) => raw?.data || raw,
      invalidatesTags: [{ type: 'FloodJob', id: 'LIST' }],
    }),

    /** GET /api/flood-detection/results/{id} — Xem lại kết quả lịch sử. */
    getGeoFloodResultById: builder.query<FloodJob, string>({
      query: (id) => ({ url: `/api/flood-detection/results/${encodeURIComponent(id)}` }),
      transformResponse: (raw: any) => {
        const data = raw?.data || raw;
        return mapGeoJobRowToFloodJob(data);
      },
      providesTags: (_r, _e, id) => [{ type: 'FloodJob', id }],
    }),

    /**
     * GET /api/netcdf/process — Xử lý NetCDF từ MinIO và lấy metadata.
     * Đây là endpoint đồng bộ trực tiếp trên Geo-processing.
     */
    processNetcdfMetadata: builder.query<
      Record<string, any>,
      { object_name: string; bucket?: string }
    >({
      query: ({ object_name, bucket }) => ({
        url: '/api/netcdf/process',
        method: 'GET',
        params: { object_name, bucket },
      }),
      transformResponse: (raw: any) => raw?.data || raw,
    }),

    /** GET /api/netcdf/jobs — Danh sách job NetCDF (nếu Geo-processing cũng quản lý DB này). */
    getGeoNetcdfJobs: builder.query<any, any>({
      query: (params) => ({
        url: '/api/netcdf/jobs',
        params: {
          pageno: params?.pageno ?? 1,
          pagesize: params?.pagesize ?? 20,
          q: params?.q,
        },
      }),
      transformResponse: (raw: any) => raw?.data || raw,
      providesTags: [{ type: 'NetcdfJob', id: 'LIST' }],
    }),

    /** POST /api/ai-jobs/land-cover/classify — Phân loại lớp phủ bề mặt (AI). */
    postGeoLandCoverAnalyze: builder.mutation<Record<string, any>, Record<string, any>>({
      query: (body) => ({
        url: '/api/ai-jobs/land-cover/classify',
        method: 'POST',
        data: body,
      }),
      transformResponse: (raw: any) => raw?.data || raw,
      invalidatesTags: [{ type: 'FloodJob', id: 'LIST' }],
    }),

    /** POST /api/ai-jobs/gap-fill — Nội suy lấp khoảng trống ảnh (AI). */
    postGeoGapFillAnalyze: builder.mutation<Record<string, any>, Record<string, any>>({
      query: (body) => ({
        url: '/api/ai-jobs/gap-fill',
        method: 'POST',
        data: body,
      }),
      transformResponse: (raw: any) => raw?.data || raw,
      invalidatesTags: [{ type: 'FloodJob', id: 'LIST' }],
    }),

    /** POST /api/ai-jobs/flood-forecast — Dự báo ngập 48 giờ (AI). */
    postGeoFloodForecastAnalyze: builder.mutation<Record<string, any>, Record<string, any>>({
      query: (body) => ({
        url: '/api/ai-jobs/flood-forecast',
        method: 'POST',
        data: body,
      }),
      transformResponse: (raw: any) => raw?.data || raw,
      invalidatesTags: [{ type: 'FloodJob', id: 'LIST' }],
    }),
    /** POST /api/flood-detection/jobs/{id}/retry — Chạy lại một job đã thất bại. */
    retryGeoFloodJob: builder.mutation<Record<string, any>, string>({
      query: (id) => ({
        url: `/api/flood-detection/jobs/${encodeURIComponent(id)}/retry`,
        method: 'POST',
      }),
      transformResponse: (raw: any) => raw?.data || raw,
      invalidatesTags: [{ type: 'FloodJob', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetGeoFloodJobsQuery,
  useGetGeoFloodJobByIdQuery,
  usePostGeoSarAnalyzeMutation,
  useGetGeoFloodResultByIdQuery,
  useLazyGetGeoFloodResultByIdQuery,
  useProcessNetcdfMetadataQuery,
  useLazyProcessNetcdfMetadataQuery,
  useGetGeoNetcdfJobsQuery,
  usePostGeoLandCoverAnalyzeMutation,
  usePostGeoGapFillAnalyzeMutation,
  usePostGeoFloodForecastAnalyzeMutation,
  useRetryGeoFloodJobMutation,
} = geoProcessingAnalysisApi;
